import React from "react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";

import {
  dateStrToObj,
  dateObjToShortStr,
} from "../../requests/utilities/helpers";
import {
  formatAxisNumber,
  formatQhClock,
  formatQhTick,
  qhHourTicks,
} from "./chartFormat";

// This function takes a number and a format type and returns the formatted string.
function formatNumber(value, format, axis, compact) {
  if (axis) return formatAxisNumber(value, format, compact);

  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`; // e.g. $1,234.45
    case "percentage":
      return `${(value * 100).toFixed(2)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return value.toFixed(2);
  }
}

// Function to get the label from the metrics data set
function getMetricLabel(metricValue, metrics) {
  // Safety checks
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const colors = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff7300", // Orange
  "#d0ed57", // Light Green
  "#a4de6c", // Lighter Green
  "#ff69b4", // Pink
  "#8dd1e1", // Light Blue
  "#83a6ed", // Lighter Blue
  "#888888", // Gray
];


const LineGraph = ({
  activeTab,
  intradayData,
  data,
  rawData,
  selectedMetrics,
  metrics,
  compareLocations,
  compareDow,
  isDashboard,
  chartHeight,
  compactAxis,
}) => {
  const yAxisWidth = compactAxis ? 40 : 60;
  // Safety checks for props
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeData = data || [];
  const safeIntradayData = intradayData || [];
  const safeRawData = rawData || [];

  // Helper to format date label based on activeTab (Daily vs Weekly)
  const formatDateLabel = (dateStr) => {
    if (typeof dateStr !== "string") {
      return String(dateStr);
    }
    const dt = dateStrToObj(dateStr);
    const formattedDate = dateObjToShortStr(dt);
    return activeTab === "Weekly" ? `Week of ${formattedDate}` : formattedDate;
  };

  // const chartMargins = isDashboard
  //   ? { top: 5, right: 30, left: 15, bottom: 5 }
  //   : { top: 5, right: 60, left: 20, bottom: 5 };

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";


  const calculateSmartMargins = (data, formatType, isDashboard) => {
    let maxLabelLength = 0;

    if (data && data.length > 0) {
      // Find the longest Y-axis label that will be displayed
      const allValues = data.flatMap(item =>
        safeSelectedMetrics.map(metric => item[metric]).filter(val => val != null)
      );

      if (allValues.length > 0) {
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);

        // Format the values exactly as they would appear on the Y-axis (with axis=true)
        const maxLabel = formatNumber(Math.round(maxVal), formatType, true, compactAxis);
        const minLabel = formatNumber(Math.round(minVal), formatType, true, compactAxis);

        maxLabelLength = Math.max(maxLabel?.length, minLabel?.length);
      }
    }

    let leftMargin;

    if (maxLabelLength <= 3) {
      leftMargin = -10;
    } else if (maxLabelLength <= 4) {
      leftMargin = -5;
    } else if (maxLabelLength <= 6) { // This will catch "$1,234" (6 chars)
      leftMargin = 5;
    } else if (maxLabelLength <= 8) { // This will catch "$12,345" (7 chars) and "$123,456" (8 chars)
      leftMargin = 15;
    } else if (maxLabelLength <= 10) { // This will catch larger dollar amounts
      leftMargin = 20;
    } else {
      leftMargin = 20;
    }

    if (compactAxis) {
      return { top: 5, right: 15, left: leftMargin, bottom: 5 };
    }
    return isDashboard
      ? { top: 5, right: 30, left: leftMargin, bottom: 5 }
      : { top: 5, right: 60, left: 20, bottom: 5 };
  };

  const smartMargins = calculateSmartMargins(safeData, formatType, isDashboard);

  // =============== DAILY DATA PROCESSING ===============
  // Group rawData by date for the "compareLocations" scenario
  // Support 'location' (store reports), 'employee' (employee reports), and 'role' (role comparison) fields
  const groupedData = safeRawData.reduce((acc, curr) => {
    if (!curr || !curr.date) return acc;
    const date = curr.date;
    if (!acc[date]) acc[date] = { date };
    const entityKey = curr.location || curr.employee || curr.role;
    if (entityKey && safeSelectedMetrics[0]) {
      acc[date][entityKey] = curr[safeSelectedMetrics[0]];
    }
    return acc;
  }, {});
  const processedData = Object.values(groupedData);

  // =============== INTRADAY DATA PROCESSING ===============
  // If we want to do "compareLocations" for intraday, we might group by qh_num
  // and store. For multi‐metric intraday, you'd also want separate lines, etc.
  // Below is a simple version reusing the same logic style:
  // Support 'location', 'employee', and 'role' fields
  const intradayLocations = [...new Set(safeIntradayData.map((d) => d && (d.location || d.employee || d.role)).filter(Boolean))];

  // Decide whether we're intraday or daily, then render accordingly
  const renderChart = () => {
    // -----------------------------------------------------------------
    // 1) If user selected "Intraday" and we actually have intradayData...
    // -----------------------------------------------------------------
    if (activeTab === "Intraday" && safeIntradayData?.length > 0) {
      // 1. If compareDow is true => one line per day_of_week
      if (compareDow) {
        // We'll pivot so each row is { qh_num, Monday: val, Tuesday: val, ... }
        const groupedByQh = safeIntradayData.reduce((acc, row) => {
          if (!row || !row.qh_num || !row.day_of_week) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { qh_num: qh };
          // For single-metric scenario; if multiple metrics, you'd do something more.
          if (safeSelectedMetrics[0]) {
            acc[qh][row.day_of_week] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});
        const processedDow = Object.values(groupedByQh).sort(
          (a, b) => a.qh_num - b.qh_num
        );

        // We define a custom order for days of the week, Monday -> Sunday
        const dayOrder = [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ];

        // Gather the distinct day_of_week values that appear in the data
        let dowList = [...new Set(safeIntradayData.map((d) => d && d.day_of_week).filter(Boolean))];
        // Sort them according to dayOrder
        dowList = dowList.sort(
          (a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b)
        );

        return (
          <LineChart
            width={500}
            height={300}
            data={processedDow}
            margin={smartMargins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              label={{ value: "Time", position: "bottom" }}
              tickFormatter={formatQhTick}
              ticks={qhHourTicks(processedDow, compactAxis ? 7 : 13)}
            />
            <YAxis
              width={yAxisWidth}
              tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)}
            />
            {formatType === "percentage" && (
              <ReferenceLine
                y={1}
                stroke="#ff6969"
                label={{ value: "100%", position: "right" }}
              />
            )}
            <Tooltip
              formatter={(val, name) => [
                `${name}: ${formatNumber(val, formatType, false)}`,
              ]}
              labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
            />
            <Legend layout="horizontal" align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: '15px' }} />
            {dowList.map((day, index) => (
              <Line
                key={day}
                type="monotone"
                dataKey={day}
                name={day}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        );
      }

      // 2. Else if compareLocations => one line per location/employee
      else if (compareLocations) {
        // Similar approach: pivot on `location` or `employee` for a single metric
        const groupedIntraday = safeIntradayData.reduce((acc, row) => {
          const entityKey = row?.location || row?.employee || row?.role;
          if (!row || !row.qh_num || !entityKey) return acc;
          const qh = row.qh_num;
          if (!acc[qh]) acc[qh] = { qh_num: qh };
          if (safeSelectedMetrics[0]) {
            acc[qh][entityKey] = row[safeSelectedMetrics[0]];
          }
          return acc;
        }, {});
        const processedIntraday = Object.values(groupedIntraday).sort(
          (a, b) => a.qh_num - b.qh_num
        );

        const intradayLocations = [
          ...new Set(safeIntradayData.map((d) => d && (d.location || d.employee || d.role)).filter(Boolean)),
        ];

        return (
          <LineChart
            width={500}
            height={300}
            data={processedIntraday}
            margin={smartMargins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="qh_num"
              label={{ value: "Time", position: "bottom" }}
              tickFormatter={formatQhTick}
              ticks={qhHourTicks(processedIntraday, compactAxis ? 7 : 13)}
            />
            <YAxis
              width={yAxisWidth}
              tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)}
            />
            {formatType === "percentage" && (
              <ReferenceLine
                y={1}
                stroke="#ff6969"
                label={{ value: "100%", position: "right" }}
              />
            )}
            <Tooltip
              formatter={(val, name) => [
                `${name}: ${formatNumber(val, formatType, false)}`,
              ]}
              labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
            />
            <Legend layout="horizontal" align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: '15px' }} />
            {intradayLocations.map((loc, index) => (
              <Line
                key={loc}
                type="monotone"
                dataKey={loc}
                name={loc}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        );
      }

      // 3. If no compareLocations and no compareDow => your existing single/multi metric logic
      else {
        if (safeSelectedMetrics.length === 1) {
          return (
            <AreaChart
              width={500}
              height={300}
              data={safeIntradayData}
              margin={smartMargins}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="qh_num"
                label={{ value: "Time", position: "bottom" }}
                tickFormatter={formatQhTick}
                ticks={qhHourTicks(safeIntradayData, compactAxis ? 7 : 13)}
              />
              <YAxis
                width={yAxisWidth}
                tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)}
              />
              {formatType === "percentage" && (
                <ReferenceLine
                  y={1}
                  stroke="#ff6969"
                  label={{ value: "100%", position: "right" }}
                />
              )}
              <Tooltip
                formatter={(val, name) => [
                  `${getMetricLabel(name, safeMetrics)}: ${formatNumber(
                    val,
                    formatType,
                    false
                  )}`,
                ]}
                labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
              />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="top"
                formatter={(val) => getMetricLabel(val, safeMetrics)}
                wrapperStyle={{ paddingBottom: '15px' }}
              />
              <Area
                type="monotone"
                dataKey={safeSelectedMetrics[0]}
                stroke="#6d28d9"
                fill="#ede9fe"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          );
        } else {
          // Multiple metrics => line chart with multiple lines
          return (
            <LineChart
              width={500}
              height={300}
              data={safeIntradayData}
              margin={smartMargins}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="qh_num"
                label={{ value: "Time", position: "bottom" }}
                tickFormatter={formatQhTick}
                ticks={qhHourTicks(safeIntradayData, compactAxis ? 7 : 13)}
              />
              <YAxis
                width={yAxisWidth}
                tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)}
              />
              {formatType === "percentage" && (
                <ReferenceLine
                  y={1}
                  stroke="#ff6969"
                  label={{ value: "100%", position: "right" }}
                />
              )}
              <Tooltip
                formatter={(val, name) => [
                  `${getMetricLabel(name, safeMetrics)}: ${formatNumber(
                    val,
                    formatType,
                    false
                  )}`,
                ]}
                labelFormatter={(labelVal) => `Time: ${formatQhClock(labelVal)}`}
              />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="top"
                formatter={(val) => getMetricLabel(val, safeMetrics)}
                wrapperStyle={{ paddingBottom: '15px' }}
              />
              {safeSelectedMetrics.map((metric, index) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  name={getMetricLabel(metric, safeMetrics)}
                  stroke={colors[index % colors.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          );
        }
      }
    }
    // -----------------------------------------------------------------
    // 2) Otherwise, do your existing DAILY chart logic
    // -----------------------------------------------------------------
    if (!compareLocations) {
      if (safeSelectedMetrics.length === 1) {
        return (
          <AreaChart
            width={500}
            height={300}
            data={safeData}
            margin={smartMargins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              label={{ value: "Date", position: "bottom" }}
              tickFormatter={(val) => {
                if (typeof val !== "string") {
                  // If it's not a string, just return it or return an empty string.
                  return "";
                }
                const dt = dateStrToObj(val);
                return `${dt.getMonth() + 1}/${dt.getDate()}`;
              }}
            />
            <YAxis
              width={yAxisWidth}
              tickFormatter={(value) => formatNumber(value, formatType, true, compactAxis)}
            />
            {formatType === "percentage" && (
              <ReferenceLine
                y={1}
                stroke="#ff6969"
                label={{ value: "100%", position: "right" }}
              />
            )}
            <Tooltip
              formatter={(val, name) => [
                `${getMetricLabel(name, safeMetrics)}: ${formatNumber(
                  val,
                  formatType,
                  false
                )}`,
              ]}
              labelFormatter={formatDateLabel}
            />
            <Legend
              layout="horizontal"
              align="center"
              verticalAlign="top"
              formatter={(val) => getMetricLabel(val, safeMetrics)}
              wrapperStyle={{ paddingBottom: '15px' }}
            />
            <Area
              type="monotone"
              dataKey={safeSelectedMetrics[0]}
              stroke="#6d28d9"
              fill="#ede9fe"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        );
      } else {
        return (
          <LineChart
            width={500}
            height={300}
            data={safeData}
            margin={smartMargins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              label={{ value: "Date", position: "bottom" }}
              tickFormatter={(val) => {
                if (typeof val !== "string") {
                  // If it's not a string, just return it or return an empty string.
                  return "";
                }
                const dt = dateStrToObj(val);
                return `${dt.getMonth() + 1}/${dt.getDate()}`;
              }}
            />
            <YAxis
              width={yAxisWidth}
              tickFormatter={(value) => formatNumber(value, formatType, true, compactAxis)}
            />
            {formatType === "percentage" && (
              <ReferenceLine
                y={1}
                stroke="#ff6969"
                label={{ value: "100%", position: "right" }}
              />
            )}
            <Tooltip
              formatter={(val, name) => [
                `${getMetricLabel(name, safeMetrics)}: ${formatNumber(
                  val,
                  formatType,
                  false
                )}`,
              ]}
              labelFormatter={formatDateLabel}
            />
            <Legend
              layout="horizontal"
              align="center"
              verticalAlign="top"
              formatter={(val) => getMetricLabel(val, safeMetrics)}
              wrapperStyle={{ paddingBottom: '15px' }}
            />
            {safeSelectedMetrics.map((metric, index) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                name={getMetricLabel(metric, safeMetrics)}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        );
      }
    } else {
      // Compare locations/employees/roles for daily
      const locations = [...new Set(safeRawData.map((d) => d && (d.location || d.employee || d.role)).filter(Boolean))];
      return (
        <LineChart
          width={500}
          height={300}
          data={processedData}
          margin={smartMargins}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            label={{ value: "Date", position: "bottom" }}
            tickFormatter={(val) => {
              if (typeof val !== "string") {
                // If it's not a string, just return it or return an empty string.
                return "";
              }
              const dt = dateStrToObj(val);
              return `${dt.getMonth() + 1}/${dt.getDate()}`;
            }}
          />
          <YAxis width={yAxisWidth} tickFormatter={(val) => formatNumber(val, formatType, true, compactAxis)} />
          {formatType === "percentage" && (
            <ReferenceLine
              y={1}
              stroke="#ff6969"
              label={{ value: "100%", position: "right" }}
            />
          )}
          <Tooltip
            formatter={(val, name) => [
              `${getMetricLabel(name, safeMetrics)}: ${formatNumber(
                val,
                formatType,
                false
              )}`,
            ]}
            labelFormatter={(str) => {
              const metricLabel = firstMetric?.label || 'Metric';
              return `${metricLabel} on ${formatDateLabel(str)}`;
            }}
          />
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="top"
            formatter={(val) => getMetricLabel(val, safeMetrics)}
            wrapperStyle={{ paddingBottom: '15px' }}
          />
          {locations.map((location, index) => (
            <Line
              key={location}
              type="monotone"
              dataKey={location}
              name={location}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      );
    }
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight ?? (isDashboard ? 300 : 400)}>
      {renderChart()}
    </ResponsiveContainer>
  );
};

export default LineGraph;
